import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../utils/firebase'
import Footer from '../components/Footer'
import MobileBackButton from '../components/MobileBackButton'
import '../styles/Careers.css'

function Careers() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    message: ''
  })

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')
  const [lastSubmitTime, setLastSubmitTime] = useState(0)
  const [submitCount, setSubmitCount] = useState(0)

  // Spam detection functions
  const containsSpam = (text) => {
    const spamKeywords = [
      'viagra', 'casino', 'lottery', 'winner', 'congratulations',
      'click here', 'free money', 'urgent', 'act now', 'limited time',
      'make money', 'work from home', 'bitcoin', 'crypto', 'investment'
    ]
    const lowerText = text.toLowerCase()
    return spamKeywords.some(keyword => lowerText.includes(keyword))
  }

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const disposableEmailDomains = [
      '10minutemail', 'tempmail', 'guerrillamail', 'mailinator',
      'throwaway', 'temp-mail', 'yopmail', 'sharklasers'
    ]
    return emailRegex.test(email) && 
           !disposableEmailDomains.some(domain => email.toLowerCase().includes(domain))
  }

  const checkRateLimit = () => {
    const now = Date.now()
    const timeSinceLastSubmit = now - lastSubmitTime
    
    // Reset counter if more than 1 hour has passed
    if (timeSinceLastSubmit > 3600000) {
      setSubmitCount(0)
      return true
    }
    
    // Allow max 2 submissions per hour
    return submitCount < 2
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }



  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('')

    // Spam protection checks
    if (!checkRateLimit()) {
      setSubmitStatus('rateLimit')
      setIsSubmitting(false)
      return
    }

    if (!isValidEmail(formData.email)) {
      setSubmitStatus('invalidEmail')
      setIsSubmitting(false)
      return
    }

    if (containsSpam(formData.message) || containsSpam(formData.role) || containsSpam(formData.name)) {
      setSubmitStatus('spam')
      setIsSubmitting(false)
      return
    }

    // Check for minimum content requirements
    if (formData.message.length < 50) {
      setSubmitStatus('tooShort')
      setIsSubmitting(false)
      return
    }

    try {
      // Add email to Firestore mail collection for Trigger Email extension
      await addDoc(collection(db, 'mail'), {
        to: ['breakout.dating@gmail.com'], // Replace with your actual email
        message: {
          subject: `Breakout Careers: ${formData.role} Application from ${formData.name}`,
          html: `
            <h3>Breakout - New Career Application</h3>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Phone:</strong> ${formData.phone || 'Not provided'}</p>
            <p><strong>Desired Role:</strong> ${formData.role}</p>
            <br>
            <p><strong>Message:</strong></p>
            <p>${formData.message.replace(/\n/g, '<br>')}</p>
            <hr>
            <p><small>Submitted: ${new Date().toLocaleString()}</small></p>
          `,
          text: `
New Career Application

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Desired Role: ${formData.role}

Message:
${formData.message}

---
Submitted: ${new Date().toLocaleString()}
Reply to: ${formData.email}
          `
        },
        replyTo: formData.email,
        timestamp: serverTimestamp()
      })

      setSubmitStatus('success')
      setIsSubmitted(true)
      
      // Update rate limiting
      setLastSubmitTime(Date.now())
      setSubmitCount(prev => prev + 1)
      
      // Reset form after 3 minutes
      setTimeout(() => {
        setIsSubmitted(false)
        setSubmitStatus('')
        setFormData({
          name: '',
          email: '',
          phone: '',
          role: '',
          message: ''
        })
      }, 180000)
    } catch (error) {
      console.error('Error sending application:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Careers - Breakout Dating App</title>
        <meta name="description" content="Join the Breakout team! We're always looking for passionate individuals who want to help people connect through shared activities." />
        <meta name="keywords" content="careers, jobs, breakout, dating app, employment, opportunities" />
      </Helmet>

      <MobileBackButton to="/" label="Back to Home" />

      <div className="careers-page">
        <div className="careers-hero">
          <div className="careers-hero-content">
            <h1>Join the Breakout Team</h1>
            <p>Help us revolutionize how people connect through shared experiences</p>
          </div>
        </div>

        <div className="careers-content">
          <div className="careers-status">
            <div className="status-card">
              <h2>Current Hiring Status</h2>
              <p className="status-message">
                We're not currently hiring for any specific positions, but we're always 
                interested in connecting with talented individuals who are passionate about 
                our mission.
              </p>
              <p>
                If you think there's a role where you could make a meaningful impact at Breakout, 
                we'd love to hear from you! Tell us about yourself and the position you envision.
              </p>
            </div>
          </div>

          <div className="careers-form-section">
            <h2>Interested in Joining Us?</h2>
            <p>Share your vision and let us know how you'd like to contribute to Breakout!</p>
            
            {isSubmitted ? (
              <div className="success-message">
                <h3>Thank you for your interest! 🎉</h3>
                <p>We've received your information and will be in touch if there's a good fit.</p>
              </div>
            ) : (
              <form className="careers-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="role">What role do you envision? *</label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      placeholder="e.g., Product Manager, iOS Developer, Marketing Specialist"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Tell us about yourself and how you'd contribute *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="6"
                    placeholder="Share your experience, skills, and vision for how you'd make an impact at Breakout..."
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                </button>

                {submitStatus === 'success' && !isSubmitted && (
                  <div className="status-message success">
                    Application submitted successfully! We'll get back to you soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="status-message error">
                    There was an error submitting your application. Please try again.
                  </div>
                )}

                {submitStatus === 'rateLimit' && (
                  <div className="status-message error">
                    Too many submissions. Please wait at least 1 hour before submitting again.
                  </div>
                )}

                {submitStatus === 'invalidEmail' && (
                  <div className="status-message error">
                    Please provide a valid email address from a permanent email provider.
                  </div>
                )}

                {submitStatus === 'spam' && (
                  <div className="status-message error">
                    Your submission contains prohibited content. Please revise and try again.
                  </div>
                )}

                {submitStatus === 'tooShort' && (
                  <div className="status-message error">
                    Please provide a more detailed message (minimum 50 characters).
                  </div>
                )}
              </form>
            )}
          </div>

          <div className="careers-culture">
            <h2>Why Join Breakout?</h2>
            <div className="culture-grid">
              <div className="culture-item">
                <h3>🚀 Innovation First</h3>
                <p>Work on cutting-edge features that change how people connect</p>
              </div>
              <div className="culture-item">
                <h3>🌱 Growth Mindset</h3>
                <p>We invest in your professional development and career growth</p>
              </div>
              <div className="culture-item">
                <h3>🤝 Meaningful Work</h3>
                <p>Help people form genuine connections and lasting relationships</p>
              </div>
              <div className="culture-item">
                <h3>🏠 Remote Friendly</h3>
                <p>Flexible work environment that supports work-life balance</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  )
}

export default Careers